import * as React from "react";
import { X, Trash2, Image as ImageIcon, Loader2, AlertTriangle, Sparkles } from "lucide-react";
import { Button } from "@/app/components/button";
import { Input } from "@/app/components/input";
import { Textarea } from "@/app/components/textarea";
import { Select } from "@/app/components/select";
import type { Ad } from "@/app/components/ad-card";
import {
  createAd,
  patchAd,
  getAdUploadUrl,
  getListingDraftUploadUrl,
  postListingDraft,
  uploadFileToSignedUrl,
  apiAdToAd,
  getCategories,
  getProfile,
  isAllowedAdImageType,
  formatListingQualityError,
  type ListingQualityPreviewPayload,
} from "@/app/api/auth-client";
import { ListingQualityPanel } from "@/app/components/listing-quality-panel";
import { useListingQualityPreview } from "@/app/hooks/use-listing-quality-preview";
import { previewUserImagesFromCount } from "@/app/utils/listing-quality-utils";
import type { ProfileAddress } from "@/app/api/auth-client";
import { compressAdImageForUpload } from "@/app/utils/compress-image-to-max-bytes";
import { enrichAdCategoryFromCatalog } from "@/app/utils/ad-utils";
import { parsePositiveShippingNumber } from "@/app/utils/shipping-form";
import type { Category } from "@/app/api/auth-client";
import {
  CategoryListingAttributeFields,
  attributeDefsForCategory,
  buildListingAttributesPayload,
  validateListingAttributeForm,
} from "@/app/components/category-listing-attributes";
import { buildCategoryTree } from "@/app/utils/category-tree";
import { toastNextTask } from "@/app/utils/toast-next-task";
import { maskBrlPriceInputChange, parseBrlPriceInput } from "@/config/locale";
import { getAIFeatures } from "@/app/api/ai-features";

/** Either a full Ad (from API) or the legacy form payload. Parent should add to state accordingly. */
export type CreateAdSubmitPayload = Ad | Omit<Ad, "id" | "createdAt">;

interface CreateAdModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (ad: CreateAdSubmitPayload) => void;
}

/** Fallback when GET /catalog/categories fails or is not configured. */
const FALLBACK_CATEGORIES = [
  "Eletrônicos",
  "Móveis",
  "Roupas",
  "Esportes",
  "Livros",
  "Brinquedos",
  "Música",
  "Outros",
];

const MAX_IMAGES = 5;

type ImageEntry = { id: string; preview: string; file: File | null };

type CreateAdFormState = {
  title: string;
  description: string;
  price: string;
  category: string;
  condition: "used" | "new";
  acceptsOffers: boolean;
  shippingWeightKg: string;
  shippingWidthCm: string;
  shippingHeightCm: string;
  shippingLengthCm: string;
};

const INITIAL_FORM_DATA: CreateAdFormState = {
  title: "",
  description: "",
  price: "",
  category: "",
  condition: "used",
  acceptsOffers: true,
  shippingWeightKg: "",
  shippingWidthCm: "",
  shippingHeightCm: "",
  shippingLengthCm: "",
};

export function CreateAdModal({ isOpen, onClose, onSubmit }: CreateAdModalProps) {
  const [formData, setFormData] = React.useState<CreateAdFormState>(() => ({ ...INITIAL_FORM_DATA }));

  const [categories, setCategories] = React.useState<Category[] | null>(null);
  const [categoriesError, setCategoriesError] = React.useState(false);
  const categoryTree = React.useMemo(
    () => (categories && categories.length > 0 ? buildCategoryTree(categories) : []),
    [categories]
  );

  const [listingAttrForm, setListingAttrForm] = React.useState<Record<string, string>>({});
  const listingAttrDefs = React.useMemo(
    () => attributeDefsForCategory(categories, formData.category),
    [categories, formData.category]
  );

  React.useEffect(() => {
    if (!isOpen) return;
    setCategoriesError(false);
    getCategories()
      .then((list) => {
        setCategories(list);
        if (list.length > 0) {
          const tree = buildCategoryTree(list);
          const firstId =
            tree[0]?.children[0]?.category_id ?? tree[0]?.root?.category_id ?? "";
          const allIds = new Set(list.map((c) => c.category_id));
          setFormData((prev) =>
            !prev.category || !allIds.has(prev.category)
              ? { ...prev, category: firstId }
              : prev
          );
        }
      })
      .catch(() => setCategoriesError(true));
  }, [isOpen]);

  const [listingDraftV2Enabled, setListingDraftV2Enabled] = React.useState(false);
  React.useEffect(() => {
    if (!isOpen) return;
    getAIFeatures()
      .then((f) => setListingDraftV2Enabled(f.listing_draft_v2))
      .catch(() => setListingDraftV2Enabled(false));
  }, [isOpen]);

  const [imageEntries, setImageEntries] = React.useState<ImageEntry[]>([]);
  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiDisclaimer, setAiDisclaimer] = React.useState<string | null>(null);
  /** v2: alternate titles returned by the AI; user can swap by tapping a chip. */
  const [aiTitleOptions, setAiTitleOptions] = React.useState<string[]>([]);
  /** v2: suggested price in BRL cents (0 = no suggestion). */
  const [aiPriceSuggestion, setAiPriceSuggestion] = React.useState<
    { cents: number; confidence: "low" | "med" | "high" | string; rationale: string } | null
  >(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [displayLocation, setDisplayLocation] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  /** Blocks double-submit while `aiLoading` is still false (loading state is deferred one tick). */
  const suggestAiInFlightRef = React.useRef(false);

  const qualityPreviewPayload = React.useMemo((): ListingQualityPreviewPayload | null => {
    if (!formData.title.trim()) return null;
    const price = parseBrlPriceInput(formData.price);
    const sw = parsePositiveShippingNumber(formData.shippingWeightKg);
    const swi = parsePositiveShippingNumber(formData.shippingWidthCm);
    const sh = parsePositiveShippingNumber(formData.shippingHeightCm);
    const sl = parsePositiveShippingNumber(formData.shippingLengthCm);
    return {
      title: formData.title,
      description: formData.description,
      price: Number.isNaN(price) || price <= 0 ? 0 : price,
      category: formData.category,
      condition: formData.condition,
      user_images: previewUserImagesFromCount(imageEntries.length),
      attributes: buildListingAttributesPayload(listingAttrDefs, listingAttrForm),
      ...(sw != null ? { shipping_weight_kg: sw } : {}),
      ...(swi != null ? { shipping_width_cm: swi } : {}),
      ...(sh != null ? { shipping_height_cm: sh } : {}),
      ...(sl != null ? { shipping_length_cm: sl } : {}),
    };
  }, [formData, imageEntries.length, listingAttrForm, listingAttrDefs]);

  const {
    result: qualityPreview,
    loading: qualityLoading,
    error: qualityError,
  } = useListingQualityPreview(qualityPreviewPayload, isOpen);

  /** Format address as "Neighborhood, City, ST" (same as BFF/Bonamassa). */
  const formatDisplayLocation = React.useCallback((addr: ProfileAddress): string => {
    const parts = [addr.neighborhood, addr.city, addr.state].filter(Boolean);
    return parts.length ? parts.join(", ") : "";
  }, []);

  React.useEffect(() => {
    if (!isOpen) return;
    setDisplayLocation(null);
    getProfile()
      .then((profile) => {
        if (!profile.addresses?.length) {
          setDisplayLocation("");
          return;
        }
        const defaultAddr =
          profile.addresses.find((a) => a.is_default) ?? profile.addresses[0];
        setDisplayLocation(formatDisplayLocation(defaultAddr) || "");
      })
      .catch(() => setDisplayLocation(""));
  }, [isOpen, formatDisplayLocation]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const remaining = MAX_IMAGES - imageEntries.length;
    const toAdd = Array.from(files).slice(0, remaining);

    for (const file of toAdd) {
      const type = (file.type || "").toLowerCase();
      if (!isAllowedAdImageType(type)) {
        alert(`${file.name}: use apenas JPG ou PNG.`);
        continue;
      }
      const compressed = await compressAdImageForUpload(file);
      if (!compressed.ok) {
        alert(`${file.name}: ${compressed.error}`);
        continue;
      }
      const resized = compressed.file;
      const preview = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve((event.target?.result as string) ?? "");
        reader.readAsDataURL(resized);
      });
      if (preview) {
        setImageEntries((prev) =>
          prev.length < MAX_IMAGES
            ? [...prev, { id: crypto.randomUUID(), preview, file: resized }]
            : prev
        );
      }
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveImage = (index: number) => {
    setImageEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSuggestWithAI = async () => {
    if (suggestAiInFlightRef.current) return;
    setSubmitError(null);
    setAiDisclaimer(null);
    const hintParts = [formData.title.trim(), formData.description.trim()].filter(Boolean);
    const extraText = hintParts.join("\n\n");

    const storagePaths: string[] = [];
    const filesToUpload = imageEntries.filter((e) => e.file);
    if (filesToUpload.length === 0 && !extraText) {
      setSubmitError("Adicione pelo menos uma foto ou preencha título/descrição para a IA.");
      return;
    }

    suggestAiInFlightRef.current = true;
    // Defer loading UI to the next task so we don't replace button children in the same stack as the click
    // (Chrome/Windows: insertBefore NotFoundError when swapping Loader vs Sparkles synchronously).
    toastNextTask(() => setAiLoading(true));
    try {
      for (const entry of filesToUpload) {
        if (!entry.file) continue;
        const type = (entry.file.type || "").toLowerCase();
        const contentType: "image/jpeg" | "image/png" =
          type === "image/png" ? "image/png" : "image/jpeg";
        const { upload_url, storage_path } = await getListingDraftUploadUrl(contentType);
        await uploadFileToSignedUrl(upload_url, entry.file, contentType);
        storagePaths.push(storage_path);
      }

      const draft = await postListingDraft({
        storage_paths: storagePaths,
        ...(extraText ? { extra_text: extraText } : {}),
        ...(formData.category ? { category_id: formData.category } : {}),
      });

      const catId = draft.suggested_category_id?.trim() || "";
      const condFromApi =
        draft.condition === "new" || draft.condition === "used" ? draft.condition : null;
      const effectiveCategoryId = catId || formData.category;
      const ship = draft.shipping_suggestion;
      // v2: capture alternate titles + price suggestion for UI affordances.
      const titleOptions = Array.isArray(draft.title_options)
        ? draft.title_options.filter((t) => typeof t === "string" && t.trim().length > 0)
        : [];
      setAiTitleOptions(titleOptions);
      const priceCents = Number(draft.suggested_price_cents) || 0;
      if (priceCents > 0) {
        setAiPriceSuggestion({
          cents: priceCents,
          confidence: draft.price_confidence || "low",
          rationale: draft.price_rationale || "",
        });
      } else {
        setAiPriceSuggestion(null);
      }

      // Defer DOM-heavy updates to the next task so the "Sugerir com IA" button finishes reconciling
      // (loader → idle) before category/attribute subtrees remount — avoids insertBefore NotFoundError (Chrome/Windows).
      toastNextTask(() => {
        setFormData((prev) => {
          const next = {
            ...prev,
            title: draft.title?.trim() || prev.title,
            description: draft.description?.trim() || prev.description,
            category: catId || prev.category,
            condition: condFromApi ?? prev.condition,
          };
          if (ship && typeof ship === "object") {
            const w = ship.weight_kg;
            const wi = ship.width_cm;
            const h = ship.height_cm;
            const l = ship.length_cm;
            next.shippingWeightKg =
              typeof w === "number" && Number.isFinite(w)
                ? String(w).replace(".", ",")
                : prev.shippingWeightKg;
            next.shippingWidthCm =
              typeof wi === "number" && Number.isFinite(wi) ? String(Math.round(wi)) : prev.shippingWidthCm;
            next.shippingHeightCm =
              typeof h === "number" && Number.isFinite(h) ? String(Math.round(h)) : prev.shippingHeightCm;
            next.shippingLengthCm =
              typeof l === "number" && Number.isFinite(l) ? String(Math.round(l)) : prev.shippingLengthCm;
          }
          return next;
        });

        const defs = attributeDefsForCategory(categories, effectiveCategoryId);
        if (draft.listing_attributes && defs.length > 0) {
          setListingAttrForm((prev) => {
            const next = { ...prev };
            for (const d of defs) {
              const v = draft.listing_attributes[d.key];
              if (typeof v === "string" && v.trim()) next[d.key] = v.trim();
              else if (typeof v === "number") next[d.key] = String(v);
            }
            return next;
          });
        }

        setAiDisclaimer(
          draft.disclaimer?.trim() ||
            "Revise peso e dimensões antes de publicar — a IA pode estimar valores incorretos."
        );
        setAiLoading(false);
        suggestAiInFlightRef.current = false;
      });
    } catch (err) {
      const status = (err as { status?: number })?.status;
      const msg =
        err instanceof Error ? err.message : "Não foi possível gerar a sugestão.";
      if (status === 503 || status === 502) {
        setSubmitError("Sugestão por IA indisponível no momento. Preencha o formulário manualmente.");
      } else {
        setSubmitError(msg);
      }
      setAiLoading(false);
      suggestAiInFlightRef.current = false;
    }
  };

  const handleMoveImage = (index: number, direction: "left" | "right") => {
    setImageEntries((prev) => {
      const next = [...prev];
      const targetIndex = direction === "left" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= next.length) return prev;
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!formData.category?.trim()) {
      setSubmitError("Selecione uma categoria.");
      return;
    }
    const attrErr = validateListingAttributeForm(listingAttrDefs, listingAttrForm);
    if (attrErr) {
      setSubmitError(attrErr);
      return;
    }
    const price = parseBrlPriceInput(formData.price);
    if (Number.isNaN(price) || price <= 0) {
      setSubmitError("Preço inválido.");
      return;
    }

    const shippingWeightKg = parsePositiveShippingNumber(formData.shippingWeightKg);
    const shippingWidthCm = parsePositiveShippingNumber(formData.shippingWidthCm);
    const shippingHeightCm = parsePositiveShippingNumber(formData.shippingHeightCm);
    const shippingLengthCm = parsePositiveShippingNumber(formData.shippingLengthCm);
    if (
      shippingWeightKg == null ||
      shippingWidthCm == null ||
      shippingHeightCm == null ||
      shippingLengthCm == null
    ) {
      setSubmitError(
        "Peso (kg) e dimensões (largura, altura e comprimento em cm) são obrigatórios para cotação de frete. Use valores maiores que zero."
      );
      return;
    }
    if (imageEntries.length === 0) {
      setSubmitError("Adicione pelo menos 1 foto do produto para publicar.");
      return;
    }

    setSubmitting(true);
    try {
      const attributesPayload = buildListingAttributesPayload(listingAttrDefs, listingAttrForm);
      const createPayload: Parameters<typeof createAd>[0] = {
        title: formData.title,
        description: formData.description,
        price: Number(price),
        category: formData.category,
        condition: formData.condition,
        accepts_offers: formData.acceptsOffers,
        user_images: [],
        shipping_weight_kg: shippingWeightKg,
        shipping_width_cm: shippingWidthCm,
        shipping_height_cm: shippingHeightCm,
        shipping_length_cm: shippingLengthCm,
        ...(Object.keys(attributesPayload).length > 0 ? { attributes: attributesPayload } : {}),
      };

      const created = await createAd(createPayload);

      const adId = created.ad_id;
      const storagePaths: string[] = [];

      for (const entry of imageEntries) {
        if (!entry.file) continue;
        const type = (entry.file.type || "").toLowerCase();
        const contentType = type === "image/png" ? "image/png" : "image/jpeg";
        const { upload_url, storage_path } = await getAdUploadUrl(adId, contentType);
        await uploadFileToSignedUrl(upload_url, entry.file, contentType);
        storagePaths.push(storage_path);
      }

      if (storagePaths.length > 0) {
        await patchAd(adId, { user_images: storagePaths });
        created.user_images = storagePaths;
      }

      const catalog = await getCategories().catch(() => [] as Category[]);
      const fullAd: Ad = enrichAdCategoryFromCatalog(
        {
          ...apiAdToAd(created),
          user_images: created.user_images?.length ? created.user_images : undefined,
        },
        catalog
      );
      onSubmit(fullAd);
      resetAndClose();
    } catch (err) {
      const qualityMsg = formatListingQualityError(err);
      if (qualityMsg) {
        setSubmitError(qualityMsg);
        return;
      }
      const status = (err as { status?: number })?.status;
      const isAuthOrUnavailable =
        status === 401 || status === 404 || status === 502 || status === 503;
      if (isAuthOrUnavailable) {
        fallbackToLocalSubmit();
      } else {
        setSubmitError(err instanceof Error ? err.message : "Não foi possível criar o anúncio.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  function fallbackToLocalSubmit() {
    const imageArray =
      imageEntries.length > 0
        ? imageEntries.map((e) => e.preview)
        : ["https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400"];
    const fallbackPrice = parseBrlPriceInput(formData.price);
    const fw = parsePositiveShippingNumber(formData.shippingWeightKg);
    const fwi = parsePositiveShippingNumber(formData.shippingWidthCm);
    const fh = parsePositiveShippingNumber(formData.shippingHeightCm);
    const fl = parsePositiveShippingNumber(formData.shippingLengthCm);
    onSubmit({
      title: formData.title,
      description: formData.description,
      price: Number.isNaN(fallbackPrice) ? 0 : fallbackPrice,
      category: formData.category || "Outros",
      location: "",
      image: imageArray[0],
      images: imageArray,
      condition: formData.condition === "new" ? "Novo" : "Seminovo",
      acceptsOffers: formData.acceptsOffers,
      ...(fw != null && fwi != null && fh != null && fl != null
        ? {
            shipping_weight_kg: fw,
            shipping_width_cm: fwi,
            shipping_height_cm: fh,
            shipping_length_cm: fl,
          }
        : {}),
    });
    resetAndClose();
  }

  /** Clear all user input (Cancel, X, backdrop, or after successful submit). */
  function resetModalForm() {
    setFormData({ ...INITIAL_FORM_DATA });
    setListingAttrForm({});
    setImageEntries([]);
    setSubmitError(null);
    setAiDisclaimer(null);
    setAiTitleOptions([]);
    setAiPriceSuggestion(null);
    setAiLoading(false);
    suggestAiInFlightRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function resetAndClose() {
    resetModalForm();
    onClose();
  }

  function handleDismiss() {
    resetModalForm();
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleDismiss} />
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <h2>Criar Novo Anúncio</h2>
          <button
            type="button"
            onClick={handleDismiss}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <ListingQualityPanel
            result={qualityPreview}
            loading={qualityLoading}
            error={qualityError}
          />
          {/* Fotos primeiro: fluxo claro para IA (mesmo layout no mobile). */}
          <div>
            <label className="block mb-2">
              Fotos do Produto ({imageEntries.length}/{MAX_IMAGES})
            </label>

            {imageEntries.length < MAX_IMAGES && (
              <div className="mb-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="flex items-center justify-center gap-3 w-full p-6 border-2 border-dashed border-border rounded-xl hover:border-primary hover:bg-accent/50 transition-all cursor-pointer group"
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="p-3 bg-primary/10 rounded-full group-hover:bg-primary/20 transition-colors">
                      <ImageIcon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-sm">Clique para adicionar fotos</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        JPG ou PNG, até 5 MB • máx. {MAX_IMAGES} fotos • {MAX_IMAGES - imageEntries.length} restantes
                      </p>
                    </div>
                  </div>
                </label>
              </div>
            )}

            {imageEntries.length > 0 && (
              <div className="grid grid-cols-4 gap-3">
                {imageEntries.map((entry, index) => (
                  <div key={entry.id} className="relative group aspect-square">
                    <img
                      src={entry.preview}
                      alt={`Imagem ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg border-2 border-border"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => handleMoveImage(index, "left")}
                          className="p-1.5 bg-white text-gray-900 rounded-md hover:bg-gray-200 transition-colors text-xs"
                          title="Mover para esquerda"
                        >
                          ←
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="p-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                        title="Remover foto"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      {index < imageEntries.length - 1 && (
                        <button
                          type="button"
                          onClick={() => handleMoveImage(index, "right")}
                          className="p-1.5 bg-white text-gray-900 rounded-md hover:bg-gray-200 transition-colors text-xs"
                          title="Mover para direita"
                        >
                          →
                        </button>
                      )}
                    </div>
                    {index === 0 && (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-primary text-primary-foreground text-xs rounded-md font-medium shadow-md">
                        Principal
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-muted-foreground mt-2">
              {imageEntries.length === 0
                ? `Adicione até ${MAX_IMAGES} fotos do seu produto. A primeira será a imagem principal.`
                : `A primeira foto será a imagem principal do anúncio. Use as setas para reordenar.`}
            </p>
          </div>

          {listingDraftV2Enabled ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={aiLoading}
              onClick={() => void handleSuggestWithAI()}
              className="gap-2"
              aria-busy={aiLoading}
            >
              <span className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden>
                <Loader2
                  className={`absolute h-4 w-4 transition-opacity duration-150 ${
                    aiLoading ? "animate-spin opacity-100" : "opacity-0"
                  }`}
                />
                <Sparkles
                  className={`h-4 w-4 transition-opacity duration-150 ${
                    aiLoading ? "opacity-0" : "opacity-100"
                  }`}
                />
              </span>
              Sugerir com IA
            </Button>
            <p className="text-xs text-muted-foreground max-w-md">
              Envie fotos e/ou o texto escrito para gerar um rascunho. Importante: confira o peso e medidas para o
              cálculo do frete.
            </p>
          </div>
          ) : null}

          <div>
            <label className="block mb-2">Título *</label>
            <Input
              required
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="ex: Câmera Vintage em Ótimo Estado"
            />
            {aiTitleOptions.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="text-xs text-muted-foreground self-center">Sugestões:</span>
                {aiTitleOptions.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, title: opt }))}
                    className="text-xs px-2 py-1 rounded-full border border-border bg-muted/40 hover:bg-muted transition-colors"
                  >
                    {opt}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div>
            <label className="block mb-2">Descrição *</label>
            <Textarea
              required
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Descreva seu item em detalhes..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-2">Preço (R$) *</label>
              <Input
                type="text"
                inputMode="numeric"
                required
                autoComplete="off"
                value={formData.price}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    price: maskBrlPriceInputChange(e.target.value),
                  }))
                }
                placeholder="0,00"
                className="tabular-nums"
              />
              {aiPriceSuggestion && aiPriceSuggestion.cents > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    const cents = aiPriceSuggestion.cents;
                    const reais = (cents / 100).toFixed(2).replace(".", ",");
                    setFormData((prev) => ({ ...prev, price: reais }));
                  }}
                  className="text-xs mt-1 underline text-primary hover:opacity-80"
                  title={aiPriceSuggestion.rationale}
                >
                  Sugestão IA: R$ {(aiPriceSuggestion.cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                  {aiPriceSuggestion.confidence !== "high" ? ` (confiança ${aiPriceSuggestion.confidence})` : ""}
                </button>
              ) : null}
              <p className="text-xs text-muted-foreground mt-1">
                Digite apenas números; o valor é formatado em reais (ex.: 9.000,00).
              </p>
            </div>

            <div>
              <label className="block mb-2">Categoria *</label>
              <Select
                value={formData.category}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => ({ ...prev, category: v }));
                  setListingAttrForm({});
                }}
              >
                {categoryTree.length > 0 ? (
                  <>
                    <option value="">Selecione uma categoria</option>
                    {categoryTree.map(({ root, children }) => (
                      <optgroup key={root.category_id} label={root.name}>
                        {children.length > 0 ? (
                          children.map((c) => (
                            <option key={c.category_id} value={c.category_id}>
                              {c.name}
                            </option>
                          ))
                        ) : (
                          <option value={root.category_id}>{root.name}</option>
                        )}
                      </optgroup>
                    ))}
                  </>
                ) : (
                  FALLBACK_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))
                )}
              </Select>
              {categoriesError && (
                <p className="text-xs text-muted-foreground mt-1">
                  Lista padrão de categorias (serviço indisponível).
                </p>
              )}
            </div>
          </div>

          <CategoryListingAttributeFields
            defs={listingAttrDefs}
            values={listingAttrForm}
            onChange={(key, value) =>
              setListingAttrForm((prev) => ({ ...prev, [key]: value }))
            }
          />

          {/* Condition: used or new */}
          <div>
            <label className="block mb-2">Condição do produto *</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="condition"
                  value="used"
                  checked={formData.condition === "used"}
                  onChange={() => setFormData((prev) => ({ ...prev, condition: "used" }))}
                  className="w-4 h-4 text-primary border-2 border-border focus:ring-2 focus:ring-primary focus:ring-offset-2"
                />
                <span>Usado</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="condition"
                  value="new"
                  checked={formData.condition === "new"}
                  onChange={() => setFormData((prev) => ({ ...prev, condition: "new" }))}
                  className="w-4 h-4 text-primary border-2 border-border focus:ring-2 focus:ring-primary focus:ring-offset-2"
                />
                <span>Novo</span>
              </label>
            </div>
          </div>

          {/* Shipping dimensions and weight (required for freight quote) */}
          <div className="space-y-3">
            <p className="text-sm font-medium">Dimensões e peso (frete) *</p>
            <p className="text-xs text-muted-foreground">
              Obrigatório para cotação e envio. Informe peso em kg e medidas da embalagem em cm (valores &gt; 0).
            </p>
            <div
              className="flex gap-3 rounded-lg border border-amber-300/90 bg-amber-50 px-3 py-2.5 text-xs text-amber-950 dark:border-amber-600/60 dark:bg-amber-950/35 dark:text-amber-50"
              role="note"
            >
              <AlertTriangle
                className="w-4 h-4 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5"
                aria-hidden
              />
              <p>
                <span className="font-semibold text-amber-900 dark:text-amber-100">Importante: </span>
                para maior precisão no cálculo do frete, insira as medidas que possam incluir a embalagem do produto.
                {aiDisclaimer ? (
                  <span className="block mt-2 font-medium text-amber-900 dark:text-amber-100">
                    {aiDisclaimer}
                  </span>
                ) : null}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-xs text-muted-foreground">Peso (kg) *</label>
                <Input
                  type="text"
                  inputMode="decimal"
                  required
                  autoComplete="off"
                  placeholder="ex: 1,5 ou 1.5"
                  value={formData.shippingWeightKg}
                  onChange={(e) => setFormData((prev) => ({ ...prev, shippingWeightKg: e.target.value }))}
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-muted-foreground">Largura (cm) *</label>
                <Input
                  type="text"
                  inputMode="decimal"
                  required
                  autoComplete="off"
                  placeholder="ex: 30"
                  value={formData.shippingWidthCm}
                  onChange={(e) => setFormData((prev) => ({ ...prev, shippingWidthCm: e.target.value }))}
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-muted-foreground">Altura (cm) *</label>
                <Input
                  type="text"
                  inputMode="decimal"
                  required
                  autoComplete="off"
                  placeholder="ex: 20"
                  value={formData.shippingHeightCm}
                  onChange={(e) => setFormData((prev) => ({ ...prev, shippingHeightCm: e.target.value }))}
                />
              </div>
              <div>
                <label className="block mb-1 text-xs text-muted-foreground">Comprimento (cm) *</label>
                <Input
                  type="text"
                  inputMode="decimal"
                  required
                  autoComplete="off"
                  placeholder="ex: 40"
                  value={formData.shippingLengthCm}
                  onChange={(e) => setFormData((prev) => ({ ...prev, shippingLengthCm: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Location (read-only): from profile default address; BFF sets it on create */}
          <div>
            <label className="block mb-2">Localização</label>
            <div className="rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground">
              {displayLocation === null
                ? "Carregando…"
                : displayLocation
                  ? displayLocation
                  : "Seu endereço principal será usado. Cadastre em Meu Perfil se ainda não tiver."}
            </div>
          </div>

          {/* Accepts Offers Checkbox */}
          <div className="bg-accent/30 rounded-lg p-4 border border-border">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.acceptsOffers}
                onChange={(e) => setFormData((prev) => ({ ...prev, acceptsOffers: e.target.checked }))}
                className="mt-1 w-5 h-5 text-primary border-2 border-border rounded focus:ring-2 focus:ring-primary focus:ring-offset-2 cursor-pointer"
              />
              <div className="flex-1">
                <p className="font-medium text-sm">Aceitar Ofertas</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Permitir que outros usuários façam ofertas em dinheiro ou trocas por produtos no seu anúncio.
                </p>
              </div>
            </label>
          </div>

          {submitError && (
            <p className="text-sm text-destructive" role="alert">
              {submitError}
            </p>
          )}

          <div className="flex gap-3 pt-4">
            <Button type="submit" className="flex-1" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Publicando...
                </>
              ) : (
                "Publicar Anúncio"
              )}
            </Button>
            <Button type="button" variant="outline" onClick={handleDismiss}>
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}